// src/services/customer/CustomerService.ts
// ERWEITERTE VERSION mit findCustomersByMandantennummer und searchCustomers

import { supabase } from '../../shared/services/supabase';
import { Customer } from '../../shared/types';

interface SoftDeleteResult {
  success: boolean;
  error?: string;
}

class CustomerService {
  /**
   * Lädt alle Kunden (ohne gelöschte)
   */
  async getAllCustomers(): Promise<Customer[]> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            id,
            full_name,
            initials
          )
        `)
        .eq('deleted', false)
        .order('company');

      if (error) {
        console.error('Error loading customers:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Unexpected error in getAllCustomers:', error);
      return [];
    }
  }

  /**
   * Lädt einen einzelnen Kunden
   */
  async getCustomerById(customerId: string, includeDeleted: boolean = false): Promise<Customer | null> {
    try {
      let query = supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            id,
            full_name,
            initials
          )
        `)
        .eq('id', customerId);

      if (!includeDeleted) {
        query = query.eq('deleted', false);
      }

      const { data, error } = await query.single();

      if (error) {
        console.error('Error loading customer:', error);
        return null;
      }

      return data as Customer;
    } catch (error) {
      console.error('Unexpected error in getCustomerById:', error);
      return null;
    }
  }

  /**
   * NEU: Findet Kunden anhand der Mandantennummer
   * Gibt ALLE passenden Kunden zurück (kann mehrere sein)
   */
  async findCustomersByMandantennummer(mandantennummer: string): Promise<Customer[]> {
    try {
      console.log(`🔍 Suche Kunden mit Mandantennummer: ${mandantennummer}`);

      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            id,
            full_name,
            initials
          )
        `)
        .eq('mandantennummer', mandantennummer)
        .eq('deleted', false)
        .order('company');

      if (error) {
        console.error('❌ Fehler beim Customer Lookup:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log(`⚠️ Kein Kunde mit Mandantennummer ${mandantennummer} gefunden`);
        return [];
      }

      console.log(`✅ ${data.length} Kunde(n) gefunden`);
      return data as Customer[];
    } catch (error) {
      console.error('Unexpected error in findCustomersByMandantennummer:', error);
      return [];
    }
  }

  /**
   * NEU: Server-seitige Volltext-Suche für Customer-Comboboxes (z.B. NewTicketModal).
   *
   * Skalierbarkeitskontext: bei ~1.200+ aktiven Kunden ist `getAllCustomers()`
   * für Picker-UIs ungeeignet (~500 KB JSON pro Modal-Oeffnung). Diese Methode
   * macht eine PostgREST-`ilike`-Suche ueber die relevantesten Felder mit Limit
   * und liefert nur die Top-Treffer zurueck.
   *
   * Frontend sollte:
   *   - mindestens 2 Zeichen vor dem ersten Aufruf erwarten
   *   - Tasteneingaben debouncen (~300ms)
   *   - das Default-Limit von 20 nicht ueberschreiten, ausser fuer Spezialfaelle
   *
   * @param query Suchstring (wird in % %-Wildcards eingebettet)
   * @param limit Maximale Trefferzahl, Default 20
   */
  async searchCustomers(query: string, limit: number = 20): Promise<Customer[]> {
    try {
      const trimmed = query.trim();
      if (trimmed.length === 0) {
        return [];
      }

      // PostgREST .or() mit ilike auf die wichtigsten Suchfelder
      const escaped = trimmed.replace(/,/g, ' ');
      const orClause = [
        `company.ilike.%${escaped}%`,
        `vorname.ilike.%${escaped}%`,
        `nachname.ilike.%${escaped}%`,
        `email.ilike.%${escaped}%`
      ].join(',');

      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            id,
            full_name,
            initials
          )
        `)
        .eq('deleted', false)
        .or(orClause)
        .order('company')
        .limit(limit);

      if (error) {
        console.error('[CustomerService.searchCustomers] Error:', error);
        return [];
      }

      return (data ?? []) as Customer[];
    } catch (error) {
      console.error('[CustomerService.searchCustomers] Unexpected error:', error);
      return [];
    }
  }

  /**
   * Markiert einen Kunden als gelöscht (Soft Delete)
   */
  async softDeleteCustomer(customerId: string): Promise<SoftDeleteResult> {
    try {
      const { error } = await supabase
        .from('customers')
        .update({ deleted: true })
        .eq('id', customerId);

      if (error) {
        console.error('Fehler beim Löschen des Kunden:', error);
        return { 
          success: false, 
          error: error.message 
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Unerwarteter Fehler beim Löschen:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  }

  /**
   * Prüft ob ein Kunde gelöscht ist
   */
  async isCustomerDeleted(customerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('deleted')
        .eq('id', customerId)
        .single();

      if (error || !data) {
        return false;
      }

      return data.deleted || false;
    } catch (error) {
      console.error('Fehler beim Prüfen des Löschstatus:', error);
      return false;
    }
  }
}

// Singleton Export
export const customerService = new CustomerService();
